// src/TeamDetail.js
import React, { useState } from "react";
import TeamChart from "./TeamChart";

const cardStyle = {
  padding: "1.25rem",
  background: "#f9fafb",
  borderRadius: "8px",
  border: "1px solid #e5e7eb",
};

const getResultBadgeStyle = (result) => {
  const normalized = (result || "").toLowerCase();
  if (normalized === "win" || normalized === "w") {
    return { bg: "#ebfbee", color: "#2b8a3e", border: "#b2f2bb" };
  }
  if (normalized === "loss" || normalized === "l") {
    return { bg: "#fff5f5", color: "#c92a2a", border: "#ffc9c9" };
  }
  return { bg: "#f1f3f5", color: "#495057", border: "#dee2e6" };
};

const TeamDetail = ({
  team,
  onBack,
  buyShares,
  sellShares,
  marketOpen = true,
  cash = 0,
  sharesOwned = 0,
}) => {
  const [quantity, setQuantity] = useState("");

  if (!team) return null;

  const teamKey = team.id || team.name;
  const currentPrice = team.price || 0;
  const schedule = team.schedule || [];

  const parsedQty = parseInt(quantity, 10);
  const isValidQty = !isNaN(parsedQty) && parsedQty > 0;
  const totalCost = isValidQty ? parsedQty * currentPrice : 0;

  const canBuy = marketOpen && isValidQty && totalCost <= cash;
  const canSell = marketOpen && isValidQty && parsedQty <= sharesOwned;

  const handleBuy = () => {
    if (!canBuy) return;
    if (buyShares) {
      buyShares(teamKey, parsedQty);
      setQuantity("");
    }
  };

  const handleSell = () => {
    if (!canSell) return;
    if (sellShares) {
      sellShares(teamKey, parsedQty);
      setQuantity("");
    }
  };

  return (
    <div
      style={{
        padding: "1.5rem",
        background: "#ffffff",
        borderRadius: "10px",
        border: "1px solid #e5e7eb",
        boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
        maxWidth: "1200px",
        margin: "0 auto",
      }}
    >
      {/* Back Button */}
      <button
        onClick={onBack}
        style={{
          marginBottom: "1.5rem",
          padding: "0.6rem 1.2rem",
          cursor: "pointer",
          background: "#ffffff",
          border: "1px solid #d1d5db",
          borderRadius: "6px",
          fontWeight: "600",
          color: "#374151",
          boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
        }}
      >
        ← Back to Market
      </button>

      {/* Header Info */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "1.25rem",
          marginBottom: "1.75rem",
          paddingBottom: "1rem",
          borderBottom: "1px solid #f3f4f6",
        }}
      >
        {team.logoURL && (
          <img
            src={team.logoURL}
            alt={team.name}
            style={{ width: 68, height: 68, objectFit: "contain" }}
          />
        )}
        <div>
          <h2 style={{ margin: 0, fontSize: "1.75rem", color: "#111827" }}>
            {team.name}
          </h2>
          <p style={{ margin: "6px 0 0 0", color: "#6b7280", fontSize: "1rem" }}>
            Conference: <strong>{team.conference || "N/A"}</strong> | Price:{" "}
            <strong style={{ color: "#111827" }}>
              ${currentPrice.toFixed(2)}
            </strong>
          </p>
        </div>
      </div>

      {/* Grid: Stats & Trading Hub */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
          gap: "1.5rem",
          marginBottom: "2rem",
        }}
      >
        {/* Holdings Card */}
        <div style={cardStyle}>
          <h4 style={{ margin: "0 0 12px 0", color: "#374151", fontSize: "1.1rem" }}>
            📊 Position & Availability
          </h4>
          <p style={{ margin: "8px 0", color: "#4b5563" }}>
            Shares Available on Market:{" "}
            <strong>{team.availableShares ?? "Unlimited"}</strong>
          </p>
          <p style={{ margin: "8px 0", color: "#4b5563" }}>
            Shares You Own:{" "}
            <strong style={{ color: "#2563eb" }}>{sharesOwned} shares</strong>
          </p>
          <p style={{ margin: "8px 0", color: "#4b5563" }}>
            Position Value:{" "}
            <strong style={{ color: "#2b8a3e" }}>
              ${(sharesOwned * currentPrice).toFixed(2)}
            </strong>
          </p>
        </div>

        {/* Quick Trade Hub Card */}
        <div style={cardStyle}>
          <h4 style={{ margin: "0 0 12px 0", color: "#374151", fontSize: "1.1rem" }}>
            ⚡ Quick Trade Hub
          </h4>
          <p style={{ margin: "0 0 12px 0", fontSize: "0.95rem", color: "#4b5563" }}>
            Your Cash: <strong>${cash.toFixed(2)}</strong>
          </p>

          <div
            style={{
              display: "flex",
              gap: "0.75rem",
              alignItems: "center",
              flexWrap: "wrap",
              marginBottom: "8px",
            }}
          >
            <input
              type="number"
              min="1"
              placeholder="Qty"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              style={{
                padding: "8px 12px",
                width: "90px",
                borderRadius: "6px",
                border: "1px solid #d1d5db",
                fontSize: "1rem",
              }}
              disabled={!marketOpen}
            />
            <button
              onClick={handleBuy}
              disabled={!canBuy}
              style={{
                padding: "8px 18px",
                background: canBuy ? "#2b8a3e" : "#9ca3af",
                color: "white",
                border: "none",
                borderRadius: "6px",
                cursor: canBuy ? "pointer" : "not-allowed",
                fontWeight: "600",
                fontSize: "0.95rem",
              }}
            >
              Buy
            </button>
            <button
              onClick={handleSell}
              disabled={!canSell}
              style={{
                padding: "8px 18px",
                background: canSell ? "#c92a2a" : "#9ca3af",
                color: "white",
                border: "none",
                borderRadius: "6px",
                cursor: canSell ? "pointer" : "not-allowed",
                fontWeight: "600",
                fontSize: "0.95rem",
              }}
            >
              Sell
            </button>
          </div>

          {/* Real-Time Total & Guard Alerts */}
          {isValidQty && marketOpen && (
            <p style={{ margin: "6px 0 0 0", fontSize: "0.85rem", color: "#4b5563" }}>
              Est. Total: <strong>${totalCost.toFixed(2)}</strong>
              {totalCost > cash && (
                <span style={{ color: "#c92a2a", marginLeft: "8px" }}>
                  (Insufficient cash)
                </span>
              )}
              {parsedQty > sharesOwned && (
                <span style={{ color: "#c92a2a", marginLeft: "8px" }}>
                  (Exceeds owned shares)
                </span>
              )}
            </p>
          )}

          {!marketOpen && (
            <p
              style={{
                color: "#c92a2a",
                fontSize: "0.85rem",
                margin: "6px 0 0 0",
                fontWeight: "500",
              }}
            >
              🔒 Market is Closed — Trading Locked
            </p>
          )}
        </div>
      </div>

      {/* Schedule & Matchups Section */}
      <div
        style={{
          marginBottom: "2rem",
          padding: "1.25rem",
          background: "#f9fafb",
          borderRadius: "8px",
          border: "1px solid #e5e7eb",
        }}
      >
        <h4 style={{ margin: "0 0 12px 0", color: "#374151", fontSize: "1.1rem" }}>
          📅 Season Schedule & Matchups
        </h4>
        {schedule.length === 0 ? (
          <p style={{ color: "#6b7280", margin: 0, fontSize: "0.95rem" }}>
            No schedule games listed for this team yet.
          </p>
        ) : (
          <div
            style={{
              display: "grid",
              gap: "10px",
              gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
            }}
          >
            {schedule.map((game, index) => {
              const b = getResultBadgeStyle(game.result);
              return (
                <div
                  key={index}
                  style={{
                    padding: "10px 12px",
                    background: "#ffffff",
                    borderRadius: "6px",
                    border: "1px solid #e5e7eb",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <div>
                    <strong style={{ fontSize: "0.9rem", color: "#374151" }}>
                      {game.week || `Game ${index + 1}`}
                    </strong>
                    <div style={{ fontSize: "0.85rem", color: "#6b7280" }}>
                      vs {game.opponent} ({game.date || "TBD"})
                    </div>
                  </div>
                  <span
                    style={{
                      fontSize: "0.75rem",
                      fontWeight: "700",
                      padding: "3px 8px",
                      borderRadius: "12px",
                      backgroundColor: b.bg,
                      color: b.color,
                      border: `1px solid ${b.border}`,
                    }}
                  >
                    {game.result ? game.result : "Upcoming"}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Price History Section */}
      <h3 style={{ marginBottom: "1rem", color: "#111827" }}>
        Price History & Performance
      </h3>
      <TeamChart team={team} />
    </div>
  );
};

export default TeamDetail;